import {
  QuestionnaireResponse,
  assessmentQuestionnaireApiHandler,
} from '@api/assessmentQuestionnaireApiHandler';
import Checkbox from '@components/Atoms/Checkbox';
import DefaultButton from '@components/Atoms/DefaultButton';
import SelectComponent from '@components/Atoms/SelectComponent';
import Modal from '@components/Modal';
import { QuestionnaireType } from '@enums/QuestionnaireTypes';
import { isEmptyValidation, noValidation } from '@helpers/validation.helper';
import { useSaveInstantHelpSpeakUpV2 } from '@hooks/api/useSpeakUp';
import { useAppDispatch } from '@hooks/useAppDispatch';
import { useAppSelector } from '@hooks/useAppSelector';
import { useSpeakUpCompanyConfig } from '@hooks/useSpeakUpCompanyConfig';
import useInput, { getCheckedValue, getInputValue } from '@hooks/useInput';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { ApiResponse } from '@interfaces/index';
import { CircularProgress } from '@mui/material';
import { MotionText, showToast } from '@shared/atoms';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { ToastMessages } from 'constants/toastMessages';
import { SPEAK_UP_MESSAGING_ERRORS } from 'custom.d';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { RootState } from 'store';
import { getInstantHelpData } from 'store/Actions/InstantHelpAction';
import { refreshConfigurationSliceActions } from 'store/Slices/RefreshCofigurationsSlice';
import styles from './index.module.scss';

function speakUpMutationErrorMessage(err: unknown): string {
  if (err instanceof Error && typeof err.message === 'string') {
    return err.message;
  }

  if (typeof err === 'object' && err !== null) {
    const o = err as Record<string, unknown>;
    if (typeof o.message === 'string') {
      return o.message;
    }

    const resp = o.response as { data?: { message?: string } } | undefined;
    if (resp?.data?.message && typeof resp.data.message === 'string') {
      return resp.data.message;
    }
  }

  return '';
}

export const CreateSpeakUp: React.FunctionComponent<CreateSpeakUpProps> = (props) => {
  const { loading, createSpeakUpOpen, handleSpeakUpModalOpen, assessmentId, questionnaireId } =
    props;
  const queryClient = useQueryClient();
  const history = useHistory();
  const mutation = useSaveInstantHelpSpeakUpV2();
  const [questionnaireNames, setQuestionnaireNames] = useState<
    { value: number; label: string }[] | []
  >([]);
  const [selectedQuestionnaireName, setSelectedQuestionnaireName] = useState<{
    value: number;
    label: string;
  }>({ value: 0, label: 'Select area' });
  const [submitted, setSubmitted] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const instantHelpData = useAppSelector((state: RootState) => state.instantHelp);
  const companyConfigData = useAppSelector((state: RootState) => state.companyConfig);
  const { buttonText: speakUpButtonText } = useSpeakUpCompanyConfig();
  const { isDesktop } = useWindowDimensions();
  const [isSharingThought, setIsSharingThought] = useState<boolean>(false);
  const [isCreateSpeakUpLimitExceeded, setIsCreateSpeakUpLimitExceeded] = useState(false);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const indicatorId = params.get('indicatorId');
  const validatedIndicatorId = indicatorId ? Number(indicatorId) : null;
  const companyName = companyConfigData?.data?.platformName || 'your';
  const company = localStorage.getItem('company');
  const parsedCompany = JSON.parse(company || '{}');

  const fetchQuestionnaires = async (): Promise<ApiResponse<QuestionnaireResponse[]>> =>
    assessmentQuestionnaireApiHandler.getAllCompanyQuestionnaires(parsedCompany.id);

  const {
    data: questionnairesData,
    isLoading: questionnairesLoading,
    error: questionnairesError,
  } = useQuery({
    queryKey: ['getAllCompanyQuestionnaires', parsedCompany.id],
    queryFn: fetchQuestionnaires,
    enabled: !!parsedCompany?.id && createSpeakUpOpen,
  });

  const questionnaires = useMemo(() => {
    if (!questionnairesData?.success) {
      return [{ value: 0, label: 'Select area' }];
    }

    const filtered = questionnairesData.responseObject
      .filter((q) => q.questionnaireV2?.questionnaireType === QuestionnaireType.CORE_PILLAR)
      .map((q) => ({
        value: q.questionnaireId,
        label: q.questionnaireV2.name,
      }));

    // return [{ value: 0, label: 'No questionnaire selected' }, ...filtered];

    return [...filtered];
  }, [questionnairesData]);

  useEffect(() => {
    if (questionnaires && questionnaireId) {
      const selectedQue = questionnaires.find((dt) => dt.value === questionnaireId);
      if (selectedQue) {
        setSelectedQuestionnaireName(selectedQue);
      }
    }
  }, [questionnaires, questionnaireId]);

  const {
    value: descriptionValue,
    isValid: descriptionIsValid,
    hasError: descriptionHasError,
    valueChangeHandler: descriptionChangeHandler,
    inputBlurHandler: descriptionBlurHandler,
    reset: descriptionReset,
  } = useInput(isEmptyValidation, getInputValue, '');

  const {
    value: shareIdentityValue = false,
    isValid: shareIdentityIsValid,
    hasError: shareIdentityHasError,
    valueChangeHandler: shareIdentityChangeHandler,
    inputBlurHandler: shareIdentityBlurHandler,
    reset: shareIdentityReset,
  } = useInput(noValidation, (value) => value, false);

  const checkboxChangeHandler = (event: any) => {
    const newValue = getCheckedValue(event);
    shareIdentityChangeHandler(newValue);
  };

  // useEffect(() => {
  //   if (createSpeakUpOpen) {
  //     getQueNames();
  //     dispatch(refreshConfigurationSliceActions.setRefreshSpeakUps(false));
  //   }
  // }, [createSpeakUpOpen]);

  const handleQuestionnaireChange = async (value: any) => {
    setSelectedQuestionnaireName(value);

    if (value?.value === 0) {
      showToast(
        ToastMessages.createSpeakUp.info.PLEASE_SELECT_A_QUESTIONNAIRE,
        undefined,
        'warning',
      );
    }
  };

  useEffect(() => {
    async function getInstantData() {
      await dispatch(getInstantHelpData());
    }

    if (!instantHelpData.isLoaded) {
      getInstantData();
    }
  }, [dispatch, instantHelpData.isLoaded]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!descriptionIsValid || !(selectedQuestionnaireName?.value > 0)) {
      showToast(
        ToastMessages.createSpeakUp.error.QUESTIONNAIRE_AND_DESCRIPTION_REQUIRED,
        undefined,
        'error',
      );

      return;
    }

    setIsSharingThought(true);

    try {
      const dt = await mutation.mutateAsync({
        assessmentId,
        message: descriptionValue,
        shareMyIdentity: shareIdentityValue,
        questionnaireId: selectedQuestionnaireName?.value,
      });

      if (dt?.success) {
        setIsSharingThought(false);
        dispatch(refreshConfigurationSliceActions.setRefreshSpeakUps(true));
        showToast(
          ToastMessages.createSpeakUp.success.MESSAGE_SHARED_SUCCESSFULLY,
          undefined,
          'success',
        );
        await queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === 'user-latest-indicators' ||
            query.queryKey[0] === 'fetchAllUserLatestAssessments',
        });
        await queryClient.invalidateQueries({ queryKey: ['thoughtsAndSpeakUps'] });

        if (validatedIndicatorId) {
          await queryClient.invalidateQueries({
            queryKey: ['indicator-history-details', validatedIndicatorId],
          });
        }

        closeModal();
      } else if (
        dt?.message?.toLowerCase() ===
        SPEAK_UP_MESSAGING_ERRORS.YOU_HAVE_EXCEEDED_YOUR_CREATE_SPEAK_UP_LIMIT
      ) {
        setIsCreateSpeakUpLimitExceeded(true);
        showToast(
          dt?.message || ToastMessages.common.UNEXPECTED_ERROR_TRY_AGAIN,
          undefined,
          'error',
        );
        setIsSharingThought(false);
      } else {
        setIsCreateSpeakUpLimitExceeded(false);
        showToast(
          dt?.message || ToastMessages.common.UNEXPECTED_ERROR_TRY_AGAIN,
          undefined,
          'error',
        );

        setIsSharingThought(false);
      }
    } catch (err: unknown) {
      const msg = speakUpMutationErrorMessage(err);
      if (
        msg.toLowerCase() === SPEAK_UP_MESSAGING_ERRORS.YOU_HAVE_EXCEEDED_YOUR_CREATE_SPEAK_UP_LIMIT
      ) {
        showToast(
          ToastMessages.createSpeakUp.error.YOU_HAVE_EXCEEDED_YOUR_CREATE_SPEAK_UP_LIMIT,
          undefined,
          'error',
        );
      } else {
        showToast(ToastMessages.common.UNEXPECTED_ERROR_TRY_AGAIN, undefined, 'error');
      }

      setIsSharingThought(false);
    }
  };

  const closeModal = () => {
    handleSpeakUpModalOpen();
    setSubmitted(false);
    descriptionReset();
    shareIdentityReset();
    setSelectedQuestionnaireName({ value: 0, label: 'Select area' });
    setIsSharingThought(false);
    setIsCreateSpeakUpLimitExceeded(false);
  };

  return (
    <Modal
      open={createSpeakUpOpen}
      setOpen={handleSpeakUpModalOpen}
      onTapBackgroundClose
      className={styles.modalRoot}
      overlay
    >
      <DefaultButton
        color="primary"
        type="button"
        className={styles.btnClose}
        onClick={() => closeModal()}
      >
        <div className={styles.btnCloseLabel}>Close</div>
        <i className={classNames('icon', 'icon-x', styles.btnCloseIcon)} />
      </DefaultButton>

      <div className={styles.helpAndSpeakupContainer}>
        <>
          <div className={styles.hsBoxContainer}>
            <div className={styles.speakUpBox}>
              {isCreateSpeakUpLimitExceeded ? (
                <div className={styles.speakUpLimitExceedContainer}>
                  <div className={styles.speakUpBoxTopContainer}>
                    <div className={styles.titleContainer}>
                      <span className={styles.title}>{speakUpButtonText}</span>
                    </div>
                  </div>
                  <div className={styles.responseLimitExceededContainer}>
                    <p>
                      You have sent high number of speak up messages recently. Please wait for the
                      administrator to respond to you to be able to follow up. If you need urgent
                      help, please{' '}
                      <span
                        onClick={() => {
                          closeModal();
                          history.push('/wellbeing');
                        }}
                      >
                        click here
                      </span>{' '}
                      to access urgent help resources.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {!submitted ? (
                    <>
                      <div className={styles.speakUpBoxTopContainer}>
                        <div className={styles.titleContainer}>
                          <span className={styles.title}>{speakUpButtonText}</span>
                          <MotionText
                            as="text"
                            className={styles.helpText}
                            text={`If you feel up to it, why not express your thoughts and feelings with ${companyName} admin team. They’re here to help.`}
                          />
                        </div>
                        <div className={styles.chatIcon} />
                      </div>
                      <MotionText
                        as="text"
                        className={styles.helpTextMobile}
                        text={`If you feel up to it, why not express your thoughts and feelings with ${companyName} admin team. They’re here to help.`}
                      />

                      <MotionText
                        className={styles.subHeadingForSelect}
                        as="text"
                        text="Which area would you like to speak up about?"
                      />

                      <div className={styles.select}>
                        <SelectComponent
                          id="questionnaireName"
                          name="questionnaireName"
                          value={questionnaires.find(
                            (option) => option.value === selectedQuestionnaireName.value,
                          )}
                          placeholder="Select area"
                          onChange={(value) => handleQuestionnaireChange(value)}
                          options={questionnaires?.filter((dt) => dt.value !== 0)}
                          className={styles.selectComponent}
                          isLoading={questionnairesLoading}
                        />
                      </div>
                      <div className={styles.formSection}>
                        <form onSubmit={handleSubmit}>
                          <div>
                            <textarea
                              name="description"
                              rows={5}
                              value={descriptionValue}
                              onChange={descriptionChangeHandler}
                              onBlur={descriptionBlurHandler}
                              placeholder="What’s on your mind?"
                              className={descriptionHasError ? 'is-invalid' : styles.textArea}
                            />
                            {descriptionHasError && <div className="invalid-feedback" />}
                          </div>
                          <div className={styles.middleDescriptionContainer}>
                            <span className={styles.middleDescription}>
                              {shareIdentityValue ? (
                                <>
                                  <span>Your name and email address will be shared</span> alongside
                                  your thoughts. If you’d like to keep your thoughts anonymous,
                                  untick the box below.
                                </>
                              ) : (
                                <>
                                  Your thoughts will be shared <span>anonymously</span>. However, if
                                  you&apos;d like your organisation to know who they&apos;re
                                  supporting, tick the box below.
                                </>
                              )}
                            </span>
                          </div>

                          <div className={styles.checkContainer}>
                            <Checkbox
                              id="shareIdentity"
                              name="shareIdentity"
                              checked={shareIdentityValue}
                              onChange={checkboxChangeHandler}
                              className={styles.roundedCheckbox}
                            />
                            <label className={styles.checkBoxTitle} htmlFor="shareIdentity">
                              Share my identity
                            </label>
                          </div>

                          <div className={styles.hsGetHelpButton}>
                            <DefaultButton
                              color="primary"
                              type="submit"
                              disabled={
                                !descriptionIsValid ||
                                isSharingThought ||
                                selectedQuestionnaireName?.value === 0
                              }
                              className={styles.defaultButton}
                            >
                              {isSharingThought ? (
                                <>
                                  <CircularProgress size={20} color="inherit" /> &nbsp;Sharing...
                                </>
                              ) : (
                                'Share Thoughts'
                              )}
                            </DefaultButton>
                          </div>
                        </form>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={styles.speakUpBoxTopContainer2}>
                        <div className={styles.titleContainer}>
                          <span className={styles.title}>{speakUpButtonText}</span>
                        </div>
                      </div>

                      <div className={styles.expressDescriptionContainer}>
                        <p className={styles.expressDescription1}>
                          Thank you for expressing your thoughts and feelings with your
                          organisation. We have shared your thoughts with them.
                        </p>
                      </div>

                      <div className={styles.checkBoxWrapper}>
                        {' '}
                        <svg
                          className={styles.checkmark}
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 52 52"
                        >
                          {' '}
                          <circle
                            className={styles.checkmarkCircle}
                            cx="26"
                            cy="26"
                            r="25"
                            fill="none"
                          />{' '}
                          <path
                            className={styles.checkmarkCheck}
                            fill="none"
                            d="M14.1 27.2l7.1 7.2 16.7-16.8"
                          />
                        </svg>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      </div>
    </Modal>
  );
};

CreateSpeakUp.defaultProps = {
  loading: false,
};

interface CreateSpeakUpProps extends React.InputHTMLAttributes<HTMLInputElement> {
  loading?: boolean;
  createSpeakUpOpen: boolean;
  handleSpeakUpModalOpen: any;
  assessmentId: number | null;
  questionnaireId: number | null;
}
